// Third-party marketing/analytics tags: the Google tag (GA4) and the
// LinkedIn Insight tag. Both are injected at runtime instead of living in
// public/index.html so they can be withheld until the visitor accepts
// cookies - same consent gate as the existing Matomo integration (see
// MatomoTracker / CookieConsent). Nothing here fires before consent.

export const GOOGLE_TAG_ID = 'G-3W3Q167WB3';
export const LINKEDIN_PARTNER_ID = '10860329';

// Signup, password-reset and verify-email links all carry their credential in
// ?token=..., and nothing in the app rewrites the address bar afterwards - so
// the URL the tags would report by default carries a live one, twice over:
// GA4's page_location and LinkedIn's own URL collection. Tokens are single-use
// and expire, but a pending reset is still a real credential, so anything sent
// to a tag goes through here first. Everything else (eventId, utm_*) is kept,
// since that is exactly what the tags exist to measure.
export const tagSafePageLocation = (href = window.location.href) => {
  try {
    const url = new URL(href);
    url.searchParams.delete('token');
    return url.toString();
  } catch {
    return href;
  }
};

let googleTagLoaded = false;
let linkedInTagLoaded = false;

// Same effect as Google's manual <head> snippet, minus the static
// <script src> line: gtag.js drains window.dataLayer as soon as it loads, so
// pushing js/config before appending the script is safe. The module flag
// makes this idempotent (React StrictMode mounts effects twice in dev).
export const initGoogleTag = () => {
  if (googleTagLoaded) return;
  googleTagLoaded = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GOOGLE_TAG_ID, { page_location: tagSafePageLocation() });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_TAG_ID}`;
  document.head.appendChild(script);
};

// Same effect as LinkedIn's snippet; appending to <head> instead of
// inserting before the first existing <script> is functionally identical.
export const initLinkedInTag = () => {
  if (linkedInTagLoaded) return;
  linkedInTagLoaded = true;

  window._linkedin_partner_id = LINKEDIN_PARTNER_ID;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);
  window.lintrk = window.lintrk || function lintrk(a, b) {
    window.lintrk.q.push([a, b]);
  };
  window.lintrk.q = window.lintrk.q || [];

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
  document.head.appendChild(script);
};

export const initAnalytics = () => {
  initGoogleTag();
  initLinkedInTag();
};

// Fired at the one point an account actually exists: the successful claim or
// fresh POST in AttendeeSignup. 'sign_up' is GA4's recommended name for it, so
// marketing gets it in the standard reports just by marking it a key event; no
// custom dimension needed for the count itself (the params below do need one
// each to be reportable).
//
// There is deliberately no equivalent lintrk('track', {conversion_id}) call.
// LinkedIn's signup conversion is configured as a URL conversion against the
// /signup/success route - see SignupSuccess - which needs nothing from this
// codebase and no per-conversion id from marketing. The Insight Tag reports
// client-side route changes as page visits (it wraps history.pushState and
// replaceState), so arriving on that route is the whole signal.
//
// Conditional on the tag having been initialized, which only happens after
// consent - trackSignup is a no-op for a visitor who never accepted, matching
// the gate on everything else here.
export const trackSignup = ({ registrationType, registeredEventName } = {}) => {
  if (!googleTagLoaded) return;

  window.gtag('event', 'sign_up', {
    method: 'email',
    registration_type: registrationType,
    page_location: tagSafePageLocation(),
    ...(registeredEventName ? { registered_event: registeredEventName } : {}),
  });
};
